import { Center, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { IconFigmaArrowLeft, IconFigmaHamburger, IconFigmaNotifications } from '@/assets/icons';
import { useGoBack } from '@/components/common/BackButton';
import { useMobileBackTo } from '@/components/layout/MobileBackContext';
import { SHOW_NOTIFICATION_DOT } from '@/config/featureFlags';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface AppHeaderProps {
  navbarOpen: boolean;
  onNavbarToggle: () => void;
}

export const AppHeader = ({ navbarOpen, onNavbarToggle }: AppHeaderProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const backTo = useMobileBackTo();
  const goBack = useGoBack(backTo ?? '/');

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '';

  return (
    <Group
      h="100%"
      px={16}
      justify="space-between"
      wrap="nowrap"
      style={{
        backgroundColor: 'var(--color-accent-secondary)',
        borderBottom: '1px solid var(--color-border-soft)'
      }}
    >
      <Group gap={10} wrap="nowrap" style={{ minWidth: 0 }}>
        {backTo ? (
          <UnstyledButton
            onClick={goBack}
            aria-label={t('common.back')}
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-on-secondary)'
            }}
          >
            <IconFigmaArrowLeft size={20} strokeWidth={1.4} />
          </UnstyledButton>
        ) : (
          <UnstyledButton
            onClick={onNavbarToggle}
            aria-expanded={navbarOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-on-secondary)'
            }}
          >
            <IconFigmaHamburger size={24} />
          </UnstyledButton>
        )}
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text
            fw={700}
            fz={14}
            c="var(--color-text-on-secondary)"
            lh={1.15}
            style={{ letterSpacing: -0.1 }}
          >
            {t('brand.name')}
          </Text>
          {/* Na mobitelu ime firme zamjenjuje tagline — desktop ga prikazuje u PageHeader traci */}
          <Text fz={11} c="var(--color-text-on-secondary)" lh={1.3} opacity={0.75} truncate>
            {isAdmin ? t('adminNav.tagline') : user?.orgName}
          </Text>
        </Stack>
      </Group>

      <Group gap={10} wrap="nowrap">
        <IconFigmaNotifications
          size={28}
          color="var(--color-text-on-secondary)"
          withDot={SHOW_NOTIFICATION_DOT}
        />
        <Center
          w={28}
          h={28}
          style={{ borderRadius: 999, backgroundColor: 'var(--color-accent-primary-soft)' }}
        >
          <Text fz={10} fw={600} c="var(--color-text-on-accent)">
            {initials}
          </Text>
        </Center>
      </Group>
    </Group>
  );
};
